import * as React from 'react';
import {
  Button,
  IconFan,
  MobileIntro,
  type IconFanItem,
} from '@relay-towing/ui';
import {
  Camera,
  Notification as NotificationGlyph,
} from '@phosphor-icons/react';
import {
  askForCamera,
  canAskForCamera,
} from '../../hooks/useDevicePermissions';
import { useNotificationSetting } from '../../hooks/useNotificationSetting';
import { dismiss } from '../../lib/dismissals';

/** Remembered on this device once it has been answered either way. */
export const WELCOME_ID = 'welcome';

/**
 * Welcome to Relay, the Figma's full page swap (123:7108, note 123:7151):
 * what the app will ask for, then Continue to be asked, or Skip
 * onboarding. A person must opt in or out, so there is no way past it but
 * those two, and it is shown once on a device.
 *
 * The frame shows five tiles: notifications, camera, photos, contacts and
 * Face ID. Only what the app can ask for is shown and asked: notifications
 * through the wrapper or the browser's push, and the camera through the
 * web view's own getUserMedia. The photo library is asked by the wrapper's
 * picker when a photo is first chosen, and the bridge has no message for
 * contacts or Face ID, so those tiles would promise a question that never
 * comes. Skip onboarding skips these asks; an owner's setup checklist has
 * its own Skip for now.
 */
export function WelcomeScreen({ onDone }: { onDone: () => void }) {
  const notifications = useNotificationSetting();
  const [asking, setAsking] = React.useState(false);

  const items: (IconFanItem & { ask: () => Promise<unknown> })[] = [
    ...(notifications.supported
      ? [
          {
            icon: NotificationGlyph,
            label: 'Notifications',
            tone: 'info' as const,
            ask: async () => notifications.setOn(true),
          },
        ]
      : []),
    ...(canAskForCamera()
      ? [
          {
            icon: Camera,
            label: 'Camera',
            tone: 'warning' as const,
            ask: askForCamera,
          },
        ]
      : []),
  ];

  const finish = () => {
    dismiss(WELCOME_ID);
    onDone();
  };

  // One at a time, so each system prompt has the screen to itself.
  const askEach = async () => {
    setAsking(true);
    for (const item of items) await item.ask();
    setAsking(false);
    finish();
  };

  return (
    <MobileIntro
      title="Welcome to Relay"
      description="Here's a few things to make your experience better"
      actions={
        <>
          <Button
            size="xl"
            pill
            variant="soft"
            loading={asking}
            onClick={() => void askEach()}
          >
            Continue
          </Button>
          <Button variant="link" disabled={asking} onClick={finish}>
            Skip onboarding
          </Button>
        </>
      }
    >
      {items.length ? (
        <IconFan
          items={items.map(({ icon, label, tone }) => ({ icon, label, tone }))}
        />
      ) : null}
    </MobileIntro>
  );
}
